import dayjs from 'dayjs';
import { getAppMeta, setAppMeta } from '../lib/db/index.ts';

export interface RateRecord {
  date: string;
  rate: number;
  source: string;
  fetchedAt?: string;
}

export interface RatesCache {
  latest: Record<string, RateRecord>;
  historical: Record<string, Record<string, number>>;
}

const CACHE_KEY = 'fresha_rates_cache_v1';
const DEXIE_KEY = 'rates_cache';

let memoryCache: RatesCache | null = null;
let isDexieHydrated = false;

function mergeRatesCaches(a: RatesCache, b: RatesCache): RatesCache {
  const mergedLatest: Record<string, RateRecord> = {
    ...(a.latest || {}),
    ...(b.latest || {}),
  };
  const mergedHistorical: Record<string, Record<string, number>> = {
    ...(a.historical || {}),
  };
  for (const [code, dates] of Object.entries(b.historical || {})) {
    mergedHistorical[code] = {
      ...(mergedHistorical[code] || {}),
      ...(dates || {}),
    };
  }
  return {
    latest: mergedLatest,
    historical: mergedHistorical,
  };
}

function loadRatesFromLocalStorage(): RatesCache {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { latest: {}, historical: {} };
  }
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { latest: {}, historical: {} };
    const parsed = JSON.parse(raw);
    return {
      latest: parsed?.latest && typeof parsed.latest === 'object' ? parsed.latest : {},
      historical: parsed?.historical && typeof parsed.historical === 'object' ? parsed.historical : {},
    };
  } catch {
    return { latest: {}, historical: {} };
  }
}

function saveRatesToLocalStorage(cache: RatesCache): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore localStorage write errors (quota exceeded, private mode, etc.)
  }
}

/**
 * Safely load rates cache from memory / localStorage with async Dexie hydration.
 */
export function loadRatesCache(): RatesCache {
  if (!memoryCache) {
    memoryCache = loadRatesFromLocalStorage();
  }

  if (!isDexieHydrated) {
    isDexieHydrated = true;
    getAppMeta<RatesCache>(DEXIE_KEY)
      .then((dexieCache) => {
        if (dexieCache) {
          memoryCache = mergeRatesCaches(memoryCache || { latest: {}, historical: {} }, dexieCache);
          saveRatesToLocalStorage(memoryCache);
        }
      })
      .catch((err) => {
        console.error('Error hydrating rates from Dexie:', err);
      });
  }

  return memoryCache;
}

/**
 * Safely save rates cache to memory, Dexie appMeta, and localStorage fallback.
 */
export function saveRatesCache(cache: RatesCache): void {
  memoryCache = cache;
  saveRatesToLocalStorage(cache);
  setAppMeta(DEXIE_KEY, cache).catch((err) => {
    console.error('Error saving rates to Dexie:', err);
  });
}

/**
 * Helper to ensure Dexie cache is hydrated before asynchronous rate lookups.
 */
export async function ensureDexieHydrated(): Promise<RatesCache> {
  const current = loadRatesCache();
  try {
    const dexieCache = await getAppMeta<RatesCache>(DEXIE_KEY);
    if (dexieCache) {
      memoryCache = mergeRatesCaches(current, dexieCache);
      saveRatesToLocalStorage(memoryCache);
      return memoryCache;
    }
  } catch (err) {
    console.error('Error hydrating rates from Dexie:', err);
  }
  return current;
}


/**
 * Normalize an unknown rate record from API responses into a structured { date, rate } pair.
 */
export function normalizeRateRecord(data: unknown): { date: string; rate: number } | null {
  if (!data || typeof data !== 'object') return null;

  const rec = data as Record<string, unknown>;
  const rawDate = rec.fecha || rec.date || rec.fechaActualizacion || rec.updatedAt;
  if (!rawDate || (typeof rawDate !== 'string' && typeof rawDate !== 'number' && !(rawDate instanceof Date))) {
    return null;
  }

  const parsedDate = dayjs(rawDate);
  if (!parsedDate.isValid()) return null;
  const date = parsedDate.format('YYYY-MM-DD');

  const candidates = [rec.promedio, rec.average, rec.valor, rec.value, rec.rate, rec.compra, rec.venta];
  let rate: number | null = null;

  for (const c of candidates) {
    if (typeof c === 'number' && !Number.isNaN(c) && c > 0) {
      rate = c;
      break;
    }
    if (typeof c === 'string') {
      const num = parseFloat(c);
      if (!Number.isNaN(num) && num > 0) {
        rate = num;
        break;
      }
    }
  }

  if (rate === null) return null;

  return { date, rate };
}

/**
 * Fetch the latest official exchange rate for USD or EUR.
 */
export async function fetchLatestRate(code: 'USD' | 'EUR'): Promise<RateRecord> {
  const endpoint = code === 'EUR' ? 'euros' : 'dolares';
  let record: { date: string; rate: number } | null = null;

  try {
    const res = await fetch(`https://ve.dolarapi.com/v1/${endpoint}/oficial`, {
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      record = normalizeRateRecord(data);
    }
  } catch {
    // Attempt fallback to proxy API
  }

  if (!record) {
    try {
      const res = await fetch(`/api/rates?type=${code.toLowerCase()}`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const json = await res.json();
        const raw = json?.data?.[code.toLowerCase()] || json?.data || json;
        record = normalizeRateRecord(raw);
      }
    } catch {
      // Fallback failed
    }
  }

  if (!record) {
    let cache = loadRatesCache();
    if (cache.latest[code]) {
      return cache.latest[code];
    }
    cache = await ensureDexieHydrated();
    if (cache.latest[code]) {
      return cache.latest[code];
    }
    throw new Error(`Failed to fetch latest rate for ${code}`);
  }

  const result: RateRecord = {
    date: record.date,
    rate: record.rate,
    source: 'api',
    fetchedAt: new Date().toISOString(),
  };

  const cache = loadRatesCache();
  cache.latest[code] = result;
  saveRatesCache(cache);

  return result;
}

/**
 * Fetch historical official rates series for USD or EUR.
 */
export async function fetchHistoricalRates(code: 'USD' | 'EUR'): Promise<Record<string, number>> {
  const endpoint = code === 'EUR' ? 'euros' : 'dolares';
  let cache = loadRatesCache();
  let currentMap = { ...(cache.historical[code] || {}) };
  if (Object.keys(currentMap).length === 0) {
    cache = await ensureDexieHydrated();
    currentMap = { ...(cache.historical[code] || {}) };
  }

  try {
    const res = await fetch(`https://ve.dolarapi.com/v1/historicos/${endpoint}/oficial`, {
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const json = await res.json();
      const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];

      for (const item of list) {
        const normalized = normalizeRateRecord(item);
        if (normalized) {
          currentMap[normalized.date] = normalized.rate;
        }
      }

      cache.historical[code] = currentMap;
      saveRatesCache(cache);
    }
  } catch {
    // If network fails, return cached historical map
  }

  return currentMap;
}

/**
 * Retrieve rate for a target date with weekend/holiday interpolation.
 */
export async function getHistoricalForDate(
  code: 'USD' | 'EUR',
  targetDate: string,
  fillMode: 'next' | 'previous' = 'next'
): Promise<RateRecord> {
  let cache = loadRatesCache();
  let map = cache.historical[code] || {};
  let dateKeys = Object.keys(map).sort();

  const today = dayjs().format('YYYY-MM-DD');
  const maxCachedDate = dateKeys.length > 0 ? dateKeys[dateKeys.length - 1] : '';

  if (dateKeys.length === 0 || (targetDate > maxCachedDate && targetDate <= today)) {
    try {
      map = await fetchHistoricalRates(code);
      dateKeys = Object.keys(map).sort();
    } catch {
      // Continue with available map
    }
  }

  if (dateKeys.length === 0) {
    cache = await ensureDexieHydrated();
    map = cache.historical[code] || {};
    dateKeys = Object.keys(map).sort();
  }

  if (map[targetDate] !== undefined) {
    return {
      date: targetDate,
      rate: map[targetDate],
      source: 'historical',
    };
  }

  if (dateKeys.length > 0) {
    if (fillMode === 'next') {
      const nextDate = dateKeys.find((d) => d >= targetDate);
      if (nextDate && map[nextDate] !== undefined) {
        return {
          date: nextDate,
          rate: map[nextDate],
          source: 'historical-next',
        };
      }
      const prevDate = [...dateKeys].reverse().find((d) => d <= targetDate);
      if (prevDate && map[prevDate] !== undefined) {
        return {
          date: prevDate,
          rate: map[prevDate],
          source: 'historical-prev',
        };
      }
    } else {
      const prevDate = [...dateKeys].reverse().find((d) => d <= targetDate);
      if (prevDate && map[prevDate] !== undefined) {
        return {
          date: prevDate,
          rate: map[prevDate],
          source: 'historical-prev',
        };
      }
      const nextDate = dateKeys.find((d) => d >= targetDate);
      if (nextDate && map[nextDate] !== undefined) {
        return {
          date: nextDate,
          rate: map[nextDate],
          source: 'historical-next',
        };
      }
    }
  }

  return fetchLatestRate(code);
}

/**
 * Determine the applicable rate record according to appointment status, pending debt, and date.
 */
export async function getApplicableRate(
  itemDate: string,
  hasPendingDebt: boolean,
  code: 'USD' | 'EUR',
  fillMode: 'next' | 'previous' = 'next'
): Promise<RateRecord> {
  const today = dayjs().format('YYYY-MM-DD');
  const normalizedItemDate = itemDate ? dayjs(itemDate).format('YYYY-MM-DD') : '';

  if (hasPendingDebt) {
    const h = await getHistoricalForDate(code, today, fillMode);
    return { ...h, source: 'historical-current-pending' };
  }

  if (!normalizedItemDate || normalizedItemDate >= today) {
    const h = await getHistoricalForDate(code, today, fillMode);
    return { ...h, source: 'historical-current' };
  }

  return getHistoricalForDate(code, normalizedItemDate, fillMode);
}

/**
 * Human-friendly mapping for rate calculation sources.
 */
export function formatRateSource(source: string): string {
  switch (source) {
    case 'historical':
      return 'tasa del día';
    case 'historical-prev':
      return 'día hábil anterior';
    case 'historical-next':
      return 'siguiente día hábil';
    case 'historical-current-pending':
      return 'tasa de hoy (saldo pendiente)';
    case 'api':
    case 'cache':
    case 'historical-current':
      return 'tasa de hoy';
    default:
      return 'tasa del día';
  }
}
